// ============================================================================
// StreetViewPanel — pilot's-eye view toward the drone (Phase 4 item 23).
//
// Data sources:
//   - track.metadata.operatorLatitude / operatorLongitude → camera origin
//   - track.latitude / longitude                          → point-at target
//
// Rendering strategy:
//   - If VITE_GOOGLE_MAPS_KEY is configured, we render a Street View Static
//     image via the public Street View Static endpoint. This is the cheap,
//     keyless-for-the-browser path — operators can see the pilot's view
//     without us needing to ship the full Google Maps JS API.
//   - If no key is configured (the default "free tier" state), we show a
//     friendly fallback with a deep link to Google Maps so the operator
//     can still drop into Street View themselves.
//
// We deliberately do NOT load the Google Maps JS API lazily — it pulls a
// few hundred kB of code and mounts a multi-tenant billable dependency
// that most operators don't want. Static imagery is enough for
// situational awareness.
// ============================================================================
import { useMemo } from 'react';
import { MapPinIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';

interface StreetViewPanelProps {
  pilotLat: number;
  pilotLng: number;
  // Point the camera at the drone so the operator sees what the pilot sees.
  targetLat: number;
  targetLng: number;
}

// Compute the initial-bearing from one lat/lng to another, in degrees true.
// Same formula the Cesium track layer uses for pilot→drone polylines.
function bearingDeg(fromLat: number, fromLng: number, toLat: number, toLng: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const toDeg = (r: number) => (r * 180) / Math.PI;
  const φ1 = toRad(fromLat);
  const φ2 = toRad(toLat);
  const Δλ = toRad(toLng - fromLng);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const θ = Math.atan2(y, x);
  return (toDeg(θ) + 360) % 360;
}

export default function StreetViewPanel({
  pilotLat,
  pilotLng,
  targetLat,
  targetLng,
}: StreetViewPanelProps) {
  // `import.meta.env` is Vite's env-var accessor. VITE_ prefix is required
  // for the var to be exposed to client bundles.
  const apiKey = (import.meta.env.VITE_GOOGLE_MAPS_KEY as string | undefined) || '';

  // Point-at-drone heading, computed once per render.
  const heading = useMemo(
    () => Math.round(bearingDeg(pilotLat, pilotLng, targetLat, targetLng)),
    [pilotLat, pilotLng, targetLat, targetLng],
  );

  // Deep link into the live Street View on maps.google.com. Works even
  // without an API key and opens the full 360° experience in a new tab.
  // `!8i8192` is the Street View zoom modifier we observed in maps.google
  // URLs — without it you get the map-only view.
  const externalUrl = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${pilotLat},${pilotLng}&heading=${heading}&pitch=0&fov=80`;

  if (!apiKey) {
    return (
      <div className="mt-3 border-t border-navy-700 pt-3">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
            Pilot view
          </h4>
          <a
            href={externalUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-accent hover:text-accent/80"
          >
            Open in Google Maps
            <ArrowTopRightOnSquareIcon className="h-3 w-3" />
          </a>
        </div>
        <div className="rounded-lg border border-navy-700 bg-navy-800/60 px-3 py-4 flex items-start gap-3">
          <MapPinIcon className="h-5 w-5 text-gray-500 flex-shrink-0" />
          <div className="text-xs text-gray-400 leading-relaxed">
            Street View imagery is unavailable — no Google Maps API key is
            configured on this deployment. Use the link above to view the
            pilot's surroundings in Google Maps, or set{' '}
            <code className="text-gray-300 bg-navy-900/80 px-1 rounded">
              VITE_GOOGLE_MAPS_KEY
            </code>{' '}
            to embed imagery here.
          </div>
        </div>
        <p className="text-[11px] text-gray-500 mt-2">
          Pilot location: {pilotLat.toFixed(4)}, {pilotLng.toFixed(4)} · looking
          {' '}{heading}° toward the drone.
        </p>
      </div>
    );
  }

  // Street View Static API — single 600×240 JPEG sized for the detail card.
  // `location` is the pilot; `heading` points at the drone; `pitch` keeps
  // the camera level. 2× device-pixel-ratio gives a crisp image on retina
  // without blowing the quota (one request per render, not per frame).
  const staticUrl =
    `https://maps.googleapis.com/maps/api/streetview?size=600x240&scale=2` +
    `&location=${pilotLat},${pilotLng}` +
    `&heading=${heading}` +
    `&pitch=0&fov=80` +
    `&key=${encodeURIComponent(apiKey)}`;

  return (
    <div className="mt-3 border-t border-navy-700 pt-3">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-xs uppercase tracking-wider text-gray-500 font-semibold">
          Pilot view
        </h4>
        <a
          href={externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[11px] text-accent hover:text-accent/80"
        >
          Interactive view
          <ArrowTopRightOnSquareIcon className="h-3 w-3" />
        </a>
      </div>
      {/* The static endpoint returns a gray "no imagery" image when Street
          View isn't covered at this location — we don't try to detect that
          client-side, we just show what Google returns. Better than hiding
          the panel outright; the operator still learns something. */}
      <img
        src={staticUrl}
        alt={`Street View looking ${heading}° from pilot location toward the drone`}
        className="w-full rounded-lg border border-navy-700 bg-navy-900 object-cover"
        loading="lazy"
      />
      <p className="text-[11px] text-gray-500 mt-2">
        Looking {heading}° from {pilotLat.toFixed(4)}, {pilotLng.toFixed(4)} toward
        the drone.
      </p>
    </div>
  );
}
